/** Directional focus navigation for gamepad and D-pad (spec §6: menus work with a gamepad). */

export type Direction = 'up' | 'down' | 'left' | 'right';

export interface Rect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface NavCandidate {
  id: number;
  rect: Rect;
}

const center = (r: Rect) => ({ x: r.left + r.width / 2, y: r.top + r.height / 2 });

/**
 * Picks the best candidate in `dir` from `from`: it must lie in that half-plane; the score
 * is the distance along the axis plus twice the sideways offset, so aligned items win.
 */
export function pickNext(
  from: Rect,
  candidates: readonly NavCandidate[],
  dir: Direction,
): number | null {
  const c = center(from);
  let best: { id: number; score: number } | null = null;
  for (const cand of candidates) {
    const t = center(cand.rect);
    const dx = t.x - c.x;
    const dy = t.y - c.y;
    const along = dir === 'right' ? dx : dir === 'left' ? -dx : dir === 'down' ? dy : -dy;
    const across = dir === 'left' || dir === 'right' ? Math.abs(dy) : Math.abs(dx);
    if (along <= 1) continue;
    const score = along + 2 * across;
    if (best === null || score < best.score) best = { id: cand.id, score };
  }
  return best?.id ?? null;
}
